import { useEffect, useMemo, useState } from 'react'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import { listRoles } from '../api/roleApi'

const DEFAULT_PAGE_SIZE = 10

function normalizeStatus(role) {
  if (Number(role?.is_archive) === 1) return 'Inactive'
  if (Number(role?.is_approved) === 0 || role?.approval_status === 'PENDING') return 'Pending Review'
  if (role?.approval_status === 'REJECTED') return 'Rejected'
  return 'Active'
}

function iconForRole(name = '') {
  const text = String(name).toLowerCase()
  if (text.includes('admin') || text.includes('security')) return 'admin_panel_settings'
  if (text.includes('audit')) return 'fact_check'
  if (text.includes('support')) return 'support_agent'
  if (text.includes('analyst') || text.includes('analytics')) return 'analytics'
  return 'shield_person'
}

function colorClassForRole(name = '') {
  const text = String(name).toLowerCase()
  if (text.includes('admin') || text.includes('security')) return 'bg-primary-fixed-dim/35 text-primary'
  if (text.includes('audit')) return 'bg-surface-variant text-on-surface-variant'
  if (text.includes('support')) return 'bg-tertiary-fixed-dim/35 text-tertiary'
  if (text.includes('analyst') || text.includes('analytics')) return 'bg-secondary-fixed-dim/35 text-secondary'
  return 'bg-secondary-fixed/40 text-on-secondary-container'
}

function formatDataScope(value) {
  const normalized = String(value || 'ALL').toUpperCase()
  if (normalized === 'SELF') return 'Self'
  if (normalized === 'TEAM') return 'Team'
  return 'All'
}

export function useRoleListScreen() {
  const [roleName, setRoleName] = useState('')
  const [roleCode, setRoleCode] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)

  const [items, setItems] = useState([])
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasMore: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  const queryParams = useMemo(() => {
    const params = {
      page,
      page_size: pageSize,
      includeTotal: true,
    }

    const searchText = [roleName.trim(), roleCode.trim()].filter(Boolean).join(' ')
    if (searchText) params.search = searchText

    if (statusFilter === 'active') params.archive = false
    if (statusFilter === 'inactive') params.archive = true

    return params
  }, [page, pageSize, roleCode, roleName, statusFilter])

  useEffect(() => {
    let active = true

    async function loadRoles() {
      setIsLoading(true)
      setError('')

      try {
        const response = await listRoles(queryParams)
        if (!active) return

        const rows = Array.isArray(response?.items) ? response.items : []
        setItems(rows)
        setMeta({
          total: Number(response?.total || 0),
          page: Number(response?.page || page),
          pageSize: Number(response?.pageSize || pageSize),
          hasMore: Boolean(response?.hasMore),
        })
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load roles.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadRoles()

    return () => {
      active = false
    }
  }, [page, pageSize, queryParams])

  const rows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        name: item.name || '-',
        code: item.role_code || '-',
        description: item.description || 'No role description provided.',
        assignedUsers: Number(item.assigned_users_count || 0),
        rightsCount: Number(item.rights_count || 0),
        dataScope: formatDataScope(item.data_scope),
        status: normalizeStatus(item),
        capability: item.capability || 'FULL_ACCESS',
        icon: iconForRole(item.name),
        iconClass: colorClassForRole(item.name),
      })),
    [items],
  )

  const totalPages = useMemo(() => {
    if (!meta.total || !meta.pageSize) return meta.hasMore ? page + 1 : Math.max(1, page)
    return Math.max(1, Math.ceil(meta.total / meta.pageSize))
  }, [meta.hasMore, meta.pageSize, meta.total, page])

  const rangeStart = useMemo(() => {
    if (!rows.length) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, rows.length])

  const rangeEnd = useMemo(() => {
    if (!rows.length) return 0
    return rangeStart + rows.length - 1
  }, [rangeStart, rows.length])

  const stats = useMemo(() => {
    const totalUsersBound = rows.reduce((sum, row) => sum + row.assignedUsers, 0)
    const totalRights = rows.reduce((sum, row) => sum + row.rightsCount, 0)
    const averageRights = rows.length ? Math.round(totalRights / rows.length) : 0
    const activeRoles = rows.filter((row) => row.status === 'Active').length
    const activeShare = rows.length ? Math.round((activeRoles / rows.length) * 100) : 0

    return {
      totalUsersBound,
      averageRights,
      activeShare,
    }
  }, [rows])

  function setRoleNameAndReset(value) {
    setRoleName(value)
    setPage(1)
  }

  function setRoleCodeAndReset(value) {
    setRoleCode(value)
    setPage(1)
  }

  function setStatusFilterAndReset(value) {
    setStatusFilter(value)
    setPage(1)
  }

  const hasActiveFilters = Boolean(roleName.trim() || roleCode.trim() || statusFilter !== 'all')

  function resetFilters() {
    setRoleName('')
    setRoleCode('')
    setStatusFilter('all')
    setPage(1)
  }

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages) return
    setPage(nextPage)
  }

  function goToPreviousPage() {
    setPage((prev) => Math.max(1, prev - 1))
  }

  function goToNextPage() {
    if (meta.hasMore || page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }

  return {
    state: {
      roleName,
      roleCode,
      statusFilter,
      hasActiveFilters,
      page,
      totalPages,
      totalCount: meta.total,
      hasMore: meta.hasMore,
      isLoading,
      error,
      rows,
      rangeStart,
      rangeEnd,
      stats,
    },
    actions: {
      setRoleName: setRoleNameAndReset,
      setRoleCode: setRoleCodeAndReset,
      setStatusFilter: setStatusFilterAndReset,
      resetFilters,
      goToPage,
      goToPreviousPage,
      goToNextPage,
    },
  }
}
